import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const BannerFlotante = () => {
  // Check sessionStorage initially
  const initialDismissed = typeof window !== 'undefined' && sessionStorage.getItem('banner_flotante_dismissed') === 'true';
  
  const [config, setConfig] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(initialDismissed);

  const loadConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/public/banner-flotante`);
      if (response.data && response.data.activo) {
        setConfig(response.data);
        setTimeout(() => setVisible(true), 300);
      }
    } catch (error) {
      console.error('Error loading banner config:', error);
    }
  }, []);

  useEffect(() => {
    if (!dismissed) {
      loadConfig();
    }
  }, [dismissed, loadConfig]);

  const handleDismiss = (e) => {
    e.stopPropagation();
    setVisible(false);
    setTimeout(() => {
      setDismissed(true);
      sessionStorage.setItem('banner_flotante_dismissed', 'true');
    }, 300);
  };

  const handleClick = () => {
    if (config?.link) {
      window.location.href = config.link;
    }
  };

  if (dismissed || !config || !config.activo) return null;

  const getAnimationClass = () => {
    switch (config.estilo_animacion) {
      case 'pulse': return 'animate-ribbon-pulse';
      case 'bounce': return 'animate-ribbon-bounce';
      case 'shake': return 'animate-ribbon-shake';
      case 'sparkle': return 'animate-ribbon-sparkle';
      default: return 'animate-ribbon-pulse';
    }
  };

  return (
    <>
      <style>{`
        @keyframes ribbon-pulse {
          0%, 100% { 
            filter: brightness(1);
            transform: rotate(-45deg) scale(1);
          }
          50% { 
            filter: brightness(1.15);
            transform: rotate(-45deg) scale(1.02);
          }
        }
        
        @keyframes ribbon-bounce {
          0%, 100% { transform: rotate(-45deg) translateY(0); }
          50% { transform: rotate(-45deg) translateY(-3px); }
        }
        
        @keyframes ribbon-shake {
          0%, 100% { transform: rotate(-45deg) translateX(0); }
          25% { transform: rotate(-45deg) translateX(-2px); }
          75% { transform: rotate(-45deg) translateX(2px); }
        }
        
        @keyframes ribbon-sparkle {
          0%, 100% { 
            box-shadow: 0 2px 10px rgba(0,0,0,0.3);
          }
          50% { 
            box-shadow: 0 2px 20px rgba(255,255,255,0.5), 0 0 30px currentColor;
          }
        }
        
        @keyframes slideInCorner {
          from {
            opacity: 0;
            transform: rotate(-45deg) translateX(-50px);
          }
          to {
            opacity: 1;
            transform: rotate(-45deg) translateX(0);
          }
        }
        
        @keyframes slideOutCorner {
          from {
            opacity: 1;
            transform: rotate(-45deg) translateX(0);
          }
          to {
            opacity: 0;
            transform: rotate(-45deg) translateX(-50px);
          }
        }
        
        .animate-ribbon-pulse { animation: ribbon-pulse 2s ease-in-out infinite; }
        .animate-ribbon-bounce { animation: ribbon-bounce 1s ease-in-out infinite; }
        .animate-ribbon-shake { animation: ribbon-shake 0.5s ease-in-out infinite; }
        .animate-ribbon-sparkle { animation: ribbon-sparkle 1.5s ease-in-out infinite; }
        
        .ribbon-enter { animation: slideInCorner 0.4s ease-out forwards; }
        .ribbon-exit { animation: slideOutCorner 0.3s ease-in forwards; }
        
        .ribbon-text {
          text-shadow: 0 1px 2px rgba(0,0,0,0.3);
        }
      `}</style>

      {/* Corner Ribbon Container */}
      <div 
        className={`fixed top-0 left-0 z-50 overflow-hidden pointer-events-none`}
        style={{ width: '150px', height: '150px' }}
      >
        {/* Diagonal Ribbon */}
        <div
          className={`
            absolute pointer-events-auto
            ${visible ? 'ribbon-enter' : 'ribbon-exit'}
            ${getAnimationClass()}
            ${config.link ? 'cursor-pointer hover:brightness-110' : ''}
          `}
          style={{
            top: '30px',
            left: '-35px',
            width: '170px',
            transform: 'rotate(-45deg)',
            transformOrigin: 'center',
            backgroundColor: config.color_fondo,
            color: config.color_texto,
            boxShadow: '0 3px 10px rgba(0,0,0,0.3)',
          }}
          onClick={config.link ? handleClick : undefined}
        >
          {/* Close Button */}
          <button
            onClick={handleDismiss}
            className="absolute -top-1 right-1 p-0.5 rounded-full hover:bg-black/20 transition-colors z-10"
            style={{ color: config.color_texto, transform: 'rotate(45deg)' }}
            aria-label="Cerrar"
            data-testid="banner-close-btn"
          >
            <X className="w-3 h-3" />
          </button>

          {/* Ribbon Content */}
          <div className="py-1.5 px-2 text-center">
            <span className="ribbon-text text-xs font-bold tracking-wide uppercase block">
              {config.texto}
            </span>
            {config.texto_secundario && (
              <span className="ribbon-text text-[9px] opacity-90 block mt-0.5">
                {config.texto_secundario}
              </span>
            )}
          </div>

          {/* Ribbon Edges (3D effect) */}
          <div 
            className="absolute -left-1 bottom-0 w-0 h-0"
            style={{
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: `8px solid ${config.color_fondo}`,
              filter: 'brightness(0.7)',
              transform: 'rotate(45deg) translateY(3px)'
            }}
          />
          <div 
            className="absolute -right-1 bottom-0 w-0 h-0"
            style={{
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: `8px solid ${config.color_fondo}`,
              filter: 'brightness(0.7)',
              transform: 'rotate(-45deg) translateY(3px)'
            }}
          />
        </div>
      </div>
    </>
  );
};

export default BannerFlotante;
